import { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Activity, Ticket as TicketIcon, FileText, Mail } from 'lucide-react';

import CircularProgress from '../components/CircularProgress';
import { PipelineBar, PipelineFlow, AnimatedNumber } from '../components/PipelineBar';
import DailyActivityChart from '../components/DailyActivityChart';
import { GlitchCardSkeleton } from '../components/GlitchSkeleton';
import AdminLogoCodeLoader from '../components/AdminLogoCodeLoader';
import { PIPELINE_COLORS } from '../theme/adminVisualSystem';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Exhibition readiness formula (frontend-derived signal).
function computeReadiness({ tickets, open_call, emails }) {
  const parts = [];

  // Ticket review progress: resolved / total
  if (tickets && tickets.total > 0) {
    const resolved = (tickets.accepted || 0) + (tickets.declined || 0) + (tickets.waitlist || 0);
    parts.push(resolved / tickets.total);
  }

  // Open call review progress
  if (open_call && open_call.total > 0) {
    const resolved =
      (open_call.under_review || 0) +
      (open_call.shortlisted || 0) +
      (open_call.accepted || 0) +
      (open_call.rejected || 0) +
      (open_call.waitlist || 0) +
      (open_call.contacted || 0);
    parts.push(resolved / open_call.total);
  }

  // Selection progress: accepted / max(1, total)
  if (open_call && open_call.total > 0) {
    parts.push((open_call.accepted || 0) / open_call.total);
  }

  // Communication: sent / max(1, total)
  if (emails && emails.total > 0) {
    parts.push((emails.sent || 0) / emails.total);
  }

  if (!parts.length) return 0;
  const avg = parts.reduce((a, b) => a + b, 0) / parts.length;
  return Math.round(avg * 100);
}

function SystemIndicator() {
  return (
    <div className="flex items-center gap-2">
      <motion.span
        className="inline-block w-1.5 h-1.5 rounded-full bg-[#90E0EF]"
        animate={{ opacity: [0.35, 1, 0.35], boxShadow: ['0 0 0px #90E0EF', '0 0 8px #90E0EF', '0 0 0px #90E0EF'] }}
        transition={{ duration: 1.8, repeat: Infinity }}
      />
      <span className="text-[9px] font-mono text-[#666] tracking-[0.2em] uppercase">SYSTEM · LIVE</span>
    </div>
  );
}

function MetricTile({ icon: Icon, label, value, accent, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      className="border border-[#1a1a1a] bg-[#0d0d0d] p-4 flex items-start gap-3 hover:border-[#90E0EF]/25 transition-colors duration-300"
    >
      <div className="mt-0.5 text-[#90E0EF] opacity-80">
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <p className="text-[9px] font-mono text-[#555] tracking-[0.2em] uppercase">{label}</p>
        <p className={`text-xl font-outfit font-semibold mt-1 tabular-nums ${accent ? 'text-[#90E0EF]' : 'text-white'}`}>
          <AnimatedNumber value={value || 0} />
        </p>
      </div>
    </motion.div>
  );
}

export default function DashboardPage() {
  const { exhibitionId, selectedExhibition } = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [emails, setEmails] = useState([]);
  // Branded boot sequence — shown for minimum time on first mount per session
  const [booting, setBooting] = useState(() => {
    try {
      return sessionStorage.getItem('admin_booted') !== 'true';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    if (!booting) return;
    const t = setTimeout(() => {
      setBooting(false);
      try { sessionStorage.setItem('admin_booted', 'true'); } catch { /* ignore */ }
    }, 1800);
    return () => clearTimeout(t);
  }, [booting]);

  useEffect(() => {
    if (!exhibitionId) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/admin/dashboard?exhibition_id=${exhibitionId}`),
      axios.get(`${API}/admin/tickets?exhibition_id=${exhibitionId}`),
      axios.get(`${API}/admin/open-call?exhibition_id=${exhibitionId}`),
      axios.get(`${API}/admin/email-logs?exhibition_id=${exhibitionId}`),
    ])
      .then(([dash, tick, oc, em]) => {
        setData(dash.data);
        setTickets(tick.data || []);
        setSubmissions(oc.data || []);
        setEmails(em.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [exhibitionId]);

  const readiness = useMemo(() => (data ? computeReadiness(data) : 0), [data]);

  // Curator metrics
  const metrics = useMemo(() => {
    if (!data) return { review: 0, selection: 0, communication: 0 };
    const oc = data.open_call || {};
    const em = data.emails || {};
    const total = oc.total || 0;

    const reviewed =
      (oc.under_review || 0) +
      (oc.shortlisted || 0) +
      (oc.accepted || 0) +
      (oc.rejected || 0) +
      (oc.waitlist || 0) +
      (oc.contacted || 0);

    const review = total ? Math.round((reviewed / total) * 100) : 0;
    const selection = total ? Math.round(((oc.accepted || 0) / total) * 100) : 0;
    const commTotal = em.total || 0;
    const communication = commTotal ? Math.round(((em.sent || 0) / commTotal) * 100) : 0;

    return { review, selection, communication };
  }, [data]);

  if (booting || loading || !data) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <AnimatePresence>
          <AdminLogoCodeLoader
            key="boot"
            label="INITIALIZING CURATOR SYSTEM"
          />
        </AnimatePresence>
        {/* Secondary glitch skeletons behind the boot (hidden during boot, visible once data lags) */}
        {!booting && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px">
              {[...Array(4)].map((_, i) => (
                <GlitchCardSkeleton key={i} height={90} />
              ))}
            </div>
            <GlitchCardSkeleton height={180} />
          </>
        )}
      </div>
    );
  }

  const { tickets: t, open_call: oc, emails: em, days_until_exhibition } = data;

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Exhibition Pulse Hero */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="border border-[#1a1a1a] bg-[#0d0d0d] p-6 sm:p-8 relative overflow-hidden"
        data-testid="exhibition-pulse-hero"
      >
        {/* Grid-overlay texture */}
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(#90E0EF 1px, transparent 1px), linear-gradient(90deg, #90E0EF 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />
        <div className="relative flex flex-col lg:flex-row gap-8 items-start lg:items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <p className="text-xs font-mono text-[#90E0EF] tracking-[0.25em] uppercase">
                EXHIBITION CONTROL CENTER
              </p>
              <SystemIndicator />
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-outfit font-semibold text-white tracking-tight">
              {selectedExhibition?.title || 'Dashboard'}
            </h1>
            {selectedExhibition?.location_name && (
              <p className="text-xs font-mono text-[#666] mt-2">
                {selectedExhibition.location_name}
                {selectedExhibition.city ? ` · ${selectedExhibition.city}` : ''}
                {selectedExhibition.country ? ` · ${selectedExhibition.country}` : ''}
              </p>
            )}
            <div className="flex items-baseline gap-6 mt-5">
              {days_until_exhibition !== null && days_until_exhibition !== undefined && (
                <div>
                  <p className="font-outfit text-4xl font-semibold text-[#90E0EF] tabular-nums">
                    <AnimatedNumber value={Math.max(0, days_until_exhibition)} />
                  </p>
                  <p className="text-[10px] font-mono text-[#666] tracking-[0.2em] uppercase mt-1">
                    {days_until_exhibition < 0 ? 'DAYS ELAPSED' : 'DAYS UNTIL OPENING'}
                  </p>
                </div>
              )}
              {selectedExhibition?.start_date && (
                <div>
                  <p className="font-outfit text-lg font-medium text-white">
                    {selectedExhibition.start_date}
                  </p>
                  <p className="text-[10px] font-mono text-[#666] tracking-[0.2em] uppercase mt-1">
                    OPENING DATE
                  </p>
                </div>
              )}
            </div>
          </div>
          <div data-testid="readiness-ring">
            <CircularProgress
              value={readiness}
              size={140}
              stroke={5}
              label="EXHIBITION READINESS"
              sublabel="OVERALL"
            />
          </div>
        </div>
      </motion.div>

      {/* Top summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px">
        <MetricTile icon={TicketIcon} label="Tickets" value={t.total} delay={0.05} />
        <MetricTile icon={FileText} label="Submissions" value={oc.total} delay={0.1} />
        <MetricTile icon={Activity} label="Shortlisted" value={oc.shortlisted} accent delay={0.15} />
        <MetricTile icon={Mail} label="Emails Sent" value={em.sent} accent delay={0.2} />
      </div>

      {/* Ticket Pipeline Card */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: 0.1 }}
        className="grid grid-cols-1 lg:grid-cols-2 gap-6"
      >
        <section
          className="border border-[#1a1a1a] bg-[#0d0d0d] p-5"
          data-testid="ticket-pipeline-card"
        >
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-[10px] font-mono text-[#90E0EF] tracking-[0.2em] uppercase">
                PIPELINE // TICKETS
              </p>
              <h3 className="font-outfit text-lg font-semibold text-white mt-1">
                Ticket Requests
              </h3>
            </div>
            <p className="font-outfit text-2xl font-semibold text-white tabular-nums">
              <AnimatedNumber value={t.total} />
            </p>
          </div>
          <PipelineBar
            total={t.total}
            segments={[
              { label: 'Pending', value: t.pending, color: PIPELINE_COLORS.pending },
              { label: 'Accepted', value: t.accepted, color: PIPELINE_COLORS.accepted, accent: true },
              { label: 'Waitlist', value: t.waitlist, color: PIPELINE_COLORS.waitlist },
              { label: 'Declined', value: t.declined, color: PIPELINE_COLORS.declined },
            ]}
          />
        </section>

        {/* Email funnel */}
        <section
          className="border border-[#1a1a1a] bg-[#0d0d0d] p-5"
          data-testid="email-funnel-card"
        >
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-[10px] font-mono text-[#90E0EF] tracking-[0.2em] uppercase">
                CHANNEL // EMAIL
              </p>
              <h3 className="font-outfit text-lg font-semibold text-white mt-1">
                Communications
              </h3>
            </div>
            <p className="font-outfit text-2xl font-semibold text-white tabular-nums">
              <AnimatedNumber value={em.total} />
            </p>
          </div>
          <PipelineBar
            total={em.total || 1}
            segments={[
              { label: 'Sent', value: em.sent, color: PIPELINE_COLORS.sent, accent: true },
              { label: 'Queued', value: em.queued || 0, color: PIPELINE_COLORS.queued },
              { label: 'Failed', value: em.failed, color: PIPELINE_COLORS.failed },
            ]}
          />
        </section>
      </motion.div>

      {/* Open Call Review Pipeline */}
      <motion.section
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: 0.15 }}
        className="border border-[#1a1a1a] bg-[#0d0d0d] p-6"
        data-testid="open-call-pipeline"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-[10px] font-mono text-[#90E0EF] tracking-[0.2em] uppercase">
              REVIEW FLOW // OPEN CALL
            </p>
            <h3 className="font-outfit text-lg font-semibold text-white mt-1">
              Curation Pipeline
            </h3>
          </div>
          <p className="text-xs font-mono text-[#666] tabular-nums">
            {oc.total} total · {oc.shortlisted + oc.accepted} advanced
          </p>
        </div>
        <PipelineFlow
          stages={[
            { key: 'new', label: 'New', value: oc.new, color: PIPELINE_COLORS.new },
            { key: 'under_review', label: 'Review', value: oc.under_review, color: PIPELINE_COLORS.under_review },
            { key: 'shortlisted', label: 'Shortlist', value: oc.shortlisted, color: PIPELINE_COLORS.shortlisted },
            { key: 'accepted', label: 'Accepted', value: oc.accepted, color: PIPELINE_COLORS.accepted },
            { key: 'waitlist', label: 'Waitlist', value: oc.waitlist, color: PIPELINE_COLORS.waitlist },
            { key: 'rejected', label: 'Rejected', value: oc.rejected, color: PIPELINE_COLORS.rejected },
          ]}
        />
      </motion.section>

      {/* Circular curator metrics */}
      <motion.section
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: 0.2 }}
        className="border border-[#1a1a1a] bg-[#0d0d0d] p-6"
        data-testid="curator-metrics"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-[10px] font-mono text-[#90E0EF] tracking-[0.2em] uppercase">
              CURATOR INSTRUMENTS
            </p>
            <h3 className="font-outfit text-lg font-semibold text-white mt-1">
              Review · Selection · Communication
            </h3>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 place-items-center">
          <CircularProgress
            value={metrics.review}
            size={130}
            label="Review Completion"
            sublabel="SUBMISSIONS"
            delay={0.1}
            testId="metric-review"
          />
          <CircularProgress
            value={metrics.selection}
            size={130}
            label="Selection Rate"
            sublabel="ACCEPTED"
            delay={0.2}
            testId="metric-selection"
          />
          <CircularProgress
            value={metrics.communication}
            size={130}
            label="Communication Progress"
            sublabel="DELIVERED"
            delay={0.3}
            testId="metric-communication"
          />
        </div>
      </motion.section>

      {/* Daily activity chart */}
      <DailyActivityChart
        tickets={tickets}
        submissions={submissions}
        emails={emails}
        days={14}
      />
    </div>
  );
}
